import { useCallback, useEffect, useMemo, useState } from 'react';
import { CourseDto } from '../../../../../../generated-api';
import CartItem from './CartItem';
import { useMenuCourses } from '../../../../../context/MenuSectionsStore';
import CompositeCartItem from './CompositeCartItem';
import { Accordion, AccordionContent, AccordionHeader, AccordionItem } from '@ordr-as/umami-react/accordion';
import { getUniqueOrderLineKey } from '../../../../../utils/orderLineUtils';
import { useCartStore } from '../../../../../context/cart/CartStore';
import { shallow } from 'zustand/shallow';
import { CartOrderLine } from '../../../../../../types/cart';

const getPaddingOfAccordion = (resolvedCourseIds: string[], courseId?: string) => {
  if (resolvedCourseIds.includes(courseId || 'No course')) {
    return 'mb-2';
  }

  return '';
};

const CartItems = () => {
  const { orderLines, giftCardPhoneNumber } = useCartStore(
    (state) => ({
      orderLines: state.cart.orderLines,
      giftCardPhoneNumber: state.giftCardPhoneNumber,
    }),
    shallow
  );

  const [activeItem, setActiveItem] = useState<number>();

  const menuCourses = useMenuCourses();
  const courses = useCourses(orderLines, menuCourses);

  const hasCourses = useMemo(() => courses.some((c) => c.courseId !== undefined), [courses]);
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>();

  const resolvedSelectedCourseIds = useMemo(() => {
    return selectedCourseIds ?? courses.map((c) => c.courseId || 'No course');
  }, [courses, selectedCourseIds]);

  const updateSelectedCourseIds = useCallback((ids: string[]) => setSelectedCourseIds(ids), []);

  useEffect(() => {
    if (activeItem !== undefined && activeItem >= orderLines.length) {
      setActiveItem(undefined);
    }
  }, [activeItem, orderLines.length]);

  const groupedCourses = useMemo(
    () =>
      courses.map((course) => (
        <AccordionItem key={course.courseId || 'No course'} value={course.courseId || 'No course'}>
          <AccordionHeader
            className={`px-4 pb-0 ${getPaddingOfAccordion(
              resolvedSelectedCourseIds,
              course.courseId
            )} [&_svg]:!text-brand-black [&_h4]:text-brand-black`}
            text={course.courseName || 'No course'}
          />
          <AccordionContent>
            <ul>
              {course.orderLines.map((ol) => {
                const globalIndex = orderLines.indexOf(ol);
                // Render composite products (items with nested components) with CompositeCartItem
                if (ol.components && ol.components.length > 0) {
                  return (
                    <CompositeCartItem
                      key={`${globalIndex}:${getUniqueOrderLineKey(ol)}`}
                      orderLine={ol}
                      itemIndex={globalIndex}
                      className="px-2"
                      setActiveItem={setActiveItem}
                      isActive={activeItem === globalIndex}
                      giftCardPhoneNumber={giftCardPhoneNumber}
                    />
                  );
                }
                // Render regular items with CartItem
                return (
                  <CartItem
                    key={`${globalIndex}:${getUniqueOrderLineKey(ol)}`}
                    orderLine={ol}
                    itemIndex={globalIndex}
                    className="px-2"
                    setActiveItem={setActiveItem}
                    isActive={activeItem === globalIndex}
                    giftCardPhoneNumber={giftCardPhoneNumber}
                  />
                );
              })}
            </ul>
          </AccordionContent>
        </AccordionItem>
      )),
    [courses, orderLines, activeItem, giftCardPhoneNumber, resolvedSelectedCourseIds]
  );

  const flatCartItems = useMemo(
    () =>
      orderLines.map((ol, index) => {
        // Render composite products (items with nested components) with CompositeCartItem
        if (ol.components && ol.components.length > 0) {
          return (
            <CompositeCartItem
              key={`${index}:${getUniqueOrderLineKey(ol)}`}
              orderLine={ol}
              itemIndex={index}
              className="px-4"
              setActiveItem={setActiveItem}
              isActive={activeItem === index}
              giftCardPhoneNumber={giftCardPhoneNumber}
            />
          );
        }
        // Render regular items with CartItem
        return (
          <CartItem
            key={`${index}:${getUniqueOrderLineKey(ol)}`}
            orderLine={ol}
            itemIndex={index}
            className="px-4"
            setActiveItem={setActiveItem}
            isActive={activeItem === index}
            giftCardPhoneNumber={giftCardPhoneNumber}
          />
        );
      }),
    [orderLines, activeItem, giftCardPhoneNumber]
  );

  return hasCourses ? (
    <Accordion type="multiple" value={resolvedSelectedCourseIds} onValueChange={updateSelectedCourseIds}>
      {groupedCourses}
    </Accordion>
  ) : (
    <ul>{flatCartItems}</ul>
  );
};

export default CartItems;

type OrderLinesByCourse = {
  courseId?: string;
  courseName?: string;
  courseSequence: number;
  orderLines: CartOrderLine[];
};

const useCourses = (orderLines: CartOrderLine[], courseDtos: CourseDto[]): OrderLinesByCourse[] => {
  const courses: { [courseId: string]: OrderLinesByCourse } = {};

  for (const orderLine of orderLines) {
    const courseDto = courseDtos.find((sectionItem) => sectionItem.courseId === orderLine.courseId);

    const courseId = courseDto?.courseId || 'No course';
    const course = courses[courseId];

    if (!course) {
      courses[courseId] = {
        courseId: courseDto?.courseId,
        courseName: courseDto?.name,
        courseSequence: courseDto?.sequence || -1,
        orderLines: [],
      };
    }

    courses[courseId]!.orderLines.push(orderLine);
  }

  return Object.values(courses).sort((a, b) => a.courseSequence - b.courseSequence);
};
